import type { TranslationDictionary } from "./types";

export const en: TranslationDictionary = {
  // ── EmailGate ──
  "page.title": "Your Proposal | Woba",
  "emailGate.placeholder": "your@email.com",
  "emailGate.invalidEmail": "Please enter a valid email",
  "emailGate.blockedEmail": "Please enter a valid corporate or personal email",
  "emailGate.loading": "Loading...",
  "emailGate.notFound": "Proposal not found",
  "emailGate.loadError":
    "Could not load the proposal. Please check the link or try again.",
  "emailGate.retry": "Try Again",
  "emailGate.proposalLoadError": "Error loading proposal. Please try again.",

  // ── ProposalCoverScreen (screen 1) ──
  "cover.ctaLabel": "View my proposal",
  "cover.talkToConsultant": "Talk to {{name}}",
  "cover.scheduleNote.bold": "Proposal valid until {{date}}.",
  "cover.scheduleNote.rest":
    "Secure your office: book an in-person or video-call visit.",
  "cover.toggle.traditional": "Traditional lease",
  "cover.toggle.woba": "Woba",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Proposal not found or expired.",

  // ── Stepper / step-by-step navigation ──
  "steps.comparison": "Woba Advantage",
  "steps.spaces.singular": "Selected Space",
  "steps.spaces.plural": "Selected Spaces",
  "steps.scheduling": "Schedule Visit",
  "steps.proposal": "Make a Proposal",
  "steps.customOffice": "Custom Office",
  "stepper.next": "Next: {{name}}",
  "nav.back": "Back",
  "nav.continue": "Continue",
  "nav.requestVisit": "Request a visit",
  "nav.backToRoomsList": "Back to the office list",

  // ── Hero copy (used by ProposalCoverScreen) ──
  "hero.subtitle":
    "Ditch the complexity of the traditional model and centralize everything in a single smart contract.",
  "hero.emailPrompt": "Your proposal is ready — enter your email to access it.",

  // ── Chat ──
  "chat.contextLabel": "Asking about",
  "chat.placeholder": "Ask about this space...",
  "chat.welcomeLine1":
    "Hello! 👋 I'm here to answer your questions about **{{spaceName}}**.",
  "chat.welcomePrice": "This space costs **R$ {{price}}/position/month**.",
  "chat.welcomeAskAnything":
    "Ask anything about this space — if I can't answer right away, we'll check with the partner and get back to you.",
  "chat.errorMessage":
    "Sorry, an error occurred while processing your message. Please try again.",
  "chat.dividerNowAbout": "Now about: {{spaceName}}",
  "chat.sendAriaLabel": "Send",
  "chat.chip.monthlyValue": "What is the monthly cost?",
  "chat.chip.parking": "Is there parking?",
  "chat.chip.internet": "What is the internet speed?",
  "chat.chip.meetingRooms": "Are meeting rooms included?",
  "chat.chip.credits": "How do Woba credits work?",
  "chat.chip.contract": "What is the contract flexibility?",
  "chat.chip.scheduleVisit": "I want to schedule a visit",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Selected Spaces",
  "spaces.optionsFor": "Options for {{companyName}}",
  "spaces.optionsForDefault": "Options for your company",
  "spaces.bestOfficesInCity":
    "We selected the best offices in {{city}} for your operation",
  "spaces.bestOptions": "We selected the best options for your operation",
  "spaces.availableOptions": "Available Options",
  "spaces.recommended": "★ Recommended",
  "spaces.combo": "Set",
  "spaces.pos": "pos",
  "spaces.scopeOffice": "Private Office",
  "spaces.scopeSpace": "Common area",
  "spaces.detail.roomId": "Room ID",
  "spaces.detail.spaceId": "Space ID",
  "spaces.detail.back": "Back",
  "spaces.detail.close": "Close",
  "spaces.detail.expandPhotos": "View all photos",
  "spaces.detail.officePhotosHeading": "{{label}} photos",
  "spaces.detail.spacePhotosHeading": "Common area photos",
  "spaces.detail.positionsHeader": "Positions",
  "spaces.detail.sizeLabel": "Size",
  "spaces.detail.pricePerPositionLabel": "Value / position",
  "spaces.detail.summary":
    "Private office to operate with {{capacity}} positions and {{size}} sqm, in the {{neighborhood}} neighborhood in {{city}}.",
  "spaces.detail.summaryNoSize":
    "Private office to operate with {{capacity}} positions, in the {{neighborhood}} neighborhood in {{city}}.",
  "spaces.detail.summaryNoNeighborhood":
    "Private office to operate with {{capacity}} positions and {{size}} sqm, in {{city}}.",
  "spaces.detail.summaryNoSizeNoNeighborhood":
    "Private office to operate with {{capacity}} positions, in {{city}}.",
  "spaces.detail.summaryComboSuffix":
    " This office is part of a set of {{total}} offices put together to meet your full request.",
  "spaces.detail.spaceSummary":
    "The space's common area has {{amenities}} amenities available.",
  "spaces.detail.aboutOfficeTitle": "About the office",
  "spaces.detail.aboutSpaceTitle": "About the common area",
  // Compliance (PED-12757): documents of the building, never of the room —
  // hence the tooltip naming the space as the subject. Positive states only.
  "spaces.detail.complianceTitle": "Compliance",
  "spaces.detail.complianceAvcb": "Fire safety certificate (AVCB)",
  "spaces.detail.complianceAvcbInfo":
    "Fire department inspection certifying the building's fire safety.",
  "spaces.detail.compliancePermit": "Operating permit",
  "spaces.detail.compliancePermitInfo":
    "City hall licence authorising the building to operate at this address.",
  "spaces.detail.amenitiesTitle": "Amenities",
  "spaces.detail.amenitiesSubtitleOffice":
    "What's available inside the private office",
  "spaces.detail.amenitiesSubtitleSpace": "What's available in the common area",
  "spaces.detail.locationTitle": "Location",
  "spaces.tabNavLabel": "Space navigation",
  "spaces.fullscreenTitle": "Spaces Location",
  "spaces.mapButton": "Show map",
  "spaces.map.pinOffices": "{{count}} offices",
  "spaces.map.showList": "Show list",
  "spaces.viewMode.cards": "View as cards",
  "spaces.viewMode.list": "View as list",
  "spaces.table.office": "Office",
  "spaces.table.monthlyValue": "Value/month",
  "spaces.table.pricePerPosition": "Cost/position",
  "spaces.table.credits": "Day passes/month",
  "spaces.table.creditBonusShort": "Earn {{credits}}",
  "spaces.mapLayers.button": "Points of interest",
  "spaces.mapLayers.viewLabel": "View",
  "spaces.mapLayers.view3d": "3D",
  "spaces.mapLayers.view2d": "2D",
  "spaces.mapLayers.poiLabel": "Points of interest",
  "spaces.card.sqm": "{{value}} sqm",
  "spaces.card.monthlyValue": "Monthly value",
  "spaces.card.creditBonus": "Earn {{credits}} day passes/month",
  "spaces.card.wantToVisit": "Select",
  "spaces.card.selected": "Selected",
  "spaces.card.visitRequested": "Visit requested",
  "spaces.card.proposalSent": "Proposal sent",
  "spaces.card.spaceHasAvcb": "Building with AVCB",
  "spaces.card.spaceHasPermit": "Building with operating permit",
  "spaces.card.complianceTooltip":
    "The building where this room is located holds this document",
  "spaces.card.viewDetails": "View details",
  "spaces.card.spacePhoto": "Common area",
  "spaces.card.comboRoomLabel": "Office {{number}}",
  "spaces.card.comboOfficesCount": "{{count}} offices",
  "spaces.card.detailsTitle": "Space details",
  "spaces.header.selectToVisit": "Choose the ideal space",
  "spaces.header.title.singular": "1 Private Office",
  "spaces.header.title.plural": "{{count}} Private Offices",
  "spaces.header.title.filteredSingular":
    "{{count}} of {{total}} Private Office",
  "spaces.header.title.filteredPlural":
    "{{count}} of {{total}} Private Offices",
  "spaces.header.lead":
    "Every option went through Woba's curation. None by chance.",
  "spaces.header.briefing.singular":
    "We chose one Private Office for your request: {{details}}.",
  "spaces.header.briefing.plural":
    "We chose a few Private Offices for your request: {{details}}.",
  "spaces.header.briefing.inCity": "in {{city}}",
  "spaces.header.briefing.positionsExact": "{{count}} positions",
  "spaces.header.briefing.contract": "{{months}}-month contract",
  "spaces.filters.sortBy": "Sort by",
  "spaces.filters.sortRecommended": "Recommended",
  "spaces.filters.sortCheapest": "Cheapest",
  "spaces.filters.sortMostPositions": "Most positions",
  "spaces.filters.capacity": "Capacity",
  "spaces.filters.priceRange": "Price range",
  "spaces.filters.size": "Size",
  "spaces.filters.capacityUpTo": "Up to {{max}} positions",
  "spaces.filters.capacityBetween": "{{min}} to {{max}} positions",
  "spaces.filters.capacityAbove": "More than {{min}} positions",
  "spaces.filters.priceUpTo": "Up to R$ {{max}}",
  "spaces.filters.priceBetween": "R$ {{min}} to R$ {{max}}",
  "spaces.filters.priceAbove": "Above R$ {{min}}",
  "spaces.filters.sizeUpTo": "Up to {{max}} sqm",
  "spaces.filters.sizeBetween": "{{min}} to {{max}} sqm",
  "spaces.filters.sizeAbove": "More than {{min}} sqm",
  "spaces.filters.noResults": "No spaces match these filters.",
  "spaces.selectedBar.hintEmpty": "Select a space to continue",
  "spaces.selectedBar.hintSelected": "Choose how you'd like to proceed",

  // ── PhotosTab ──
  "photos.prevPhoto": "Previous photo",
  "photos.nextPhoto": "Next photo",
  "photos.noImage": "No image available",
  "photos.didYouLike": "Did you like it?",
  "photos.liked": "Liked",
  "photos.disliked": "Disliked",
  "photos.positions": "positions",
  "photos.perPosition": "R$ {{price}} / position",
  "photos.priceOnRequest": "Price on request",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee included",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Amenity information not available.",
  "amenities.showOthers": "Others ({{count}})",
  "amenities.showLess": "Show less",

  // ── RoomEmbeddedMap (distance calculator) ──
  "map.calculateDistance": "Calculate the distance between the space and...",
  "map.placeholder": "e.g., Av. Paulista, 1000",
  "map.searching": "Searching...",
  "map.calculate": "Calculate",
  "map.distancesFrom": "Distances from",
  "map.addressNotProvided": "Address not provided",
  "map.selectedNoAddress": "Address not provided for this option",
  "map.recenter": "Recenter",
  "map.recenterTitle": "Recenter map",
  "map.clickToInteract": "Click to interact with the map",
  "map.locationNotFound":
    "We couldn't find that address. Try a different search.",
  "advantages.goToSlide": "Go to highlight {{n}}",
  "visitDialog.title": "Schedule visits",
  "visitDialog.description":
    "At this point, we would reach out to the selected Partners to confirm visit availability for their rooms.",
  "poi.noDescription": "No description",

  // ── ComparisonSection ──
  "comparison.heading": "The *smartest* way to lease and manage offices",

  // ── ComparisonSection ("Woba advantage" content) ──
  "advantage.heading": "Having an office *can be easy*.",
  "advantage.subheadingPre": "With Woba, you get access to",
  "advantage.subheadingBold": "a lot more than an office",
  "advantage.subheadingPost":
    ": everything simple, practical, and hassle-free. And you don't pay anything extra for it.",
  "advantage.cardsAriaLabel": "Woba advantages",
  "advantage.card1.title": "Best price guaranteed",
  "advantage.card1.descPre": "With Woba,",
  "advantage.card1.descBold": "pay the same monthly value",
  "advantage.card1.descPost":
    "as negotiating directly with the partner, with up to 15% negotiated off — plus the whole network.",
  "advantage.card1.sealAlt": "Best price guaranteed seal",
  "advantage.card1.tagline":
    "Found a better price, in or out of the network? We'll match it.",
  "advantage.card2.title": "Much more than an office",
  "advantage.card2.stat": "3,000+ spaces · 400+ cities",
  "advantage.card2.descPre": "Your team gets access to",
  "advantage.card2.descBold": "meeting rooms and coworking day passes",
  "advantage.card2.descPost":
    "across more than 3,000 spaces in the Woba Network, nationwide, at no extra cost.",
  "advantage.card2.badge": "Earn hours to use across the network",
  "advantage.card3.title": "Your facilities team, superpowered",
  "advantage.card3.descPre": "A team of",
  "advantage.card3.descBold": "AI agents",
  "advantage.card3.descPost":
    "handles your operation — access, bookings, maintenance and contracts — all sorted in a single invoice, no red tape.",
  "advantage.card4.title": "Office as a service",
  "advantage.card4.descBold": "The flexibility you need:",
  "advantage.card4.descPost":
    "scale up, scale down, or move cities whenever the business needs it. No penalty, no CAPEX, no construction, no long-term contract.",

  // ── CostBreakdownDrawer ("Understand the price" badge on the card) ──
  "costBreakdown.badge": "Understand the price",
  "costBreakdown.badgeWithScope": "Understand the price - {{scope}}",
  "costBreakdown.drawerTitle": "All of it, in a single invoice.",
  "costBreakdown.drawerSubtitle":
    "Your team focuses on the business, not the building.",
  "costBreakdown.compositionTitle": "How your lease works at Woba",
  "costBreakdown.compositionBodyPre":
    "This monthly price is made up of the same 3 cost groups as a traditional lease. Here, they come together as a single value, with a single due date, with no spreadsheet to reconcile.",
  "costBreakdown.compositionBodyBold": "All of it, in a single invoice.",
  "costBreakdown.totalLabel": "Monthly total",
  "costBreakdown.detailTitle": "How a traditional lease works",
  "costBreakdown.footnotePre":
    "With a traditional lease, your monthly bill splits across 3 big groups. Each of those groups has several items inside it, which adds up to {{count}} items to manage: {{count}} vendors, {{count}} contracts, each with its own due date and its own point of contact. In the end, it's your team that carries that weight every month.",
  "costBreakdown.category.rent": "Rent + Condo Fee + Property Tax",
  "costBreakdown.category.services": "Operational Services",
  "costBreakdown.category.support": "Support & Depreciation",
  "costBreakdown.item.rentBase": "Base rent",
  "costBreakdown.item.condo": "Condo fee",
  "costBreakdown.item.iptu": "Property tax",
  "costBreakdown.item.energy": "Energy",
  "costBreakdown.item.staff": "Operations staff",
  "costBreakdown.item.cleaning": "Cleaning",
  "costBreakdown.item.internet": "Internet / Telecom",
  "costBreakdown.item.maintenance": "Maintenance",
  "costBreakdown.item.water": "Water",
  "costBreakdown.item.furniture": "Furniture (depreciation)",
  "costBreakdown.item.amenities": "Coffee / Amenities",
  "costBreakdown.item.insurance": "Insurance",
  "costBreakdown.item.admin": "Admin / Overhead",

  // ── CTASection / Footer ──
  "cta.tagline": "The smartest way to hire and manage offices",
  "cta.contact": "Contact",
  "cta.proposal": "Proposal",
  "cta.validUntil": "Valid until:",
  "cta.validTBD": "To be defined",
  "cta.copyright": "© {{year}} Woba. All rights reserved.",

  // ── Logos marquee (ProposalCoverScreen footer) ──
  "logos.socialProof": "Join thousands of companies that trust Woba",

  // ── VisitSchedulingStep ──
  "schedule.overline": "Schedule your visit",
  "schedule.title": "You *choose* how you get to know it.",
  "schedule.subtitle":
    "The deeper you go, the more you understand how Woba works in practice — and how much it saves {{companyName}}.",
  "schedule.subtitleNoCompany":
    "The deeper you go, the more you understand how Woba works in practice — and how much it can save you.",
  "schedule.instruction":
    "Choose the day, time, and format of the visit for each space.",
  "schedule.dateTimeLabel": "Day and time",
  "schedule.previousDates": "Previous days",
  "schedule.nextDates": "Next days",
  "schedule.modalityLabel": "Visit format",
  "schedule.modalityInPerson": "In person",
  "schedule.modalityOnline": "Video call",
  "schedule.modalityTestDrive": "Test-drive",
  "schedule.modalityExplainerInPerson":
    "Real and up close. See the space for yourself, check out the structure up close and get a feel for your team's day-to-day there.",
  "schedule.modalityExplainerOnline":
    "Practical and immediate. Book a video call visit to see the office and ask questions directly about the space.",
  "schedule.modalityExplainerTestDrive":
    "Complete, no rush. Use the office for a full day with your team before deciding, at no cost.",
  "schedule.testDriveLearnMore": "Learn more",
  "schedule.testDriveDrawerTitle": "1-day test-drive",
  "schedule.testDriveDrawerSubtitle":
    "Complete, no rush. Use the office for a full day with your team before deciding, at no cost.*",
  "schedule.testDriveDrawerPhotoAlt":
    "Team working together in a shared Woba space",
  "schedule.testDriveHowItWorksTitle": "How it works",
  "schedule.testDriveStep1":
    "Pick an available date on the calendar — no set time, it's the whole day.",
  "schedule.testDriveStep2":
    "Woba confirms availability directly with the space.",
  "schedule.testDriveStep3":
    "You get the confirmation and bring your team on the agreed day.",
  "schedule.testDriveStep4":
    "Decide based on the real experience, not just photos.",
  "schedule.testDriveBenefitsTitle": "What you get",
  "schedule.testDriveBenefit1":
    "Test your team's day-to-day in the space before closing.",
  "schedule.testDriveBenefit2": "No cost — the visit is on Woba.",
  "schedule.testDriveBenefit3":
    "More confidence to decide, backed by real day-to-day data.",
  "schedule.testDriveClosingNote":
    "* Subject to the space's confirmation, within its business hours.",
  "schedule.testDriveNoteLabel": "Why do you want to test this space?",
  "schedule.testDriveNotePlaceholder":
    "Tell us what caught your attention in this office and what you want to confirm with the test-drive",
  "schedule.testDriveSingleRoomError":
    "Test-drive is for 1 office at a time. Remove the others to continue.",
  "schedule.visitorCountLabel": "How many people are coming?",
  "schedule.visitorCountPlaceholder": "E.g.: 4 people",
  "schedule.valueBullet1":
    "Best price guaranteed — the same as negotiating directly.",
  "schedule.valueBullet2": "Earn credits for meeting rooms and coworking.",
  "schedule.valueBullet3":
    "Flexibility to scale up, scale down, or switch cities.",
  "schedule.visitTypeInPerson": "In-person visit",
  "schedule.visitTypeOnline": "Video call visit",
  "schedule.visitTypeTestDrive": "Test-drive (1 day)",
  "schedule.slotTakenHint": "This time is already taken by another space",
  "schedule.fewSlotsAlert":
    "Few time slots left. Pick yours and lock in your visit.",
  "schedule.confirm": "Request visit",
  "schedule.subStepAriaLabel": "Scheduling steps",
  "schedule.subStepWhen": "Day and time",
  "schedule.subStepData": "Your details",
  "schedule.dataOverline": "Almost there",
  "schedule.dataTitle": "Who's *joining*?",
  "schedule.dataSubtitle": "Complete the details of who's joining the visit.",
  "schedule.hintIncomplete": "Select day, time, and format for each space",
  "schedule.hintContactIncomplete": "Fill in the fields to continue",
  "schedule.fieldEmail": "Email",
  "schedule.fieldEmailPlaceholder": "you@email.com",
  "schedule.fieldEmailError": "Enter a valid email address",
  "schedule.fieldWhatsappError": "Enter a valid phone number",
  "schedule.addAnotherSpace": "Add another space to the visit",
  "schedule.addParticipant": "Add another participant",
  "schedule.removeVisit": "Remove this space from the visit",
  "schedule.removeParticipant": "Remove participant",
  "schedule.participantLabel": "Participant {{n}}",
  "schedule.fieldName": "Name",
  "schedule.fieldNamePlaceholder": "Your name",
  "schedule.fieldJobTitle": "Job title",
  "schedule.fieldJobTitlePlaceholder": "Your job title",
  "schedule.fieldWhatsapp": "WhatsApp (recommended)",
  "schedule.fieldWhatsappRequired": "WhatsApp",
  "schedule.fieldWhatsappPlaceholder": "(11) 90000-0000",
  "schedule.fieldCpf": "CPF (Brazilian tax ID)",
  "schedule.fieldCpfPlaceholder": "000.000.000-00",
  "schedule.fieldCpfError": "Enter a valid CPF",
  "schedule.fieldCpfHint":
    "Used only to clear your access to the space on the test-drive day.",
  "privacy.consentPrefix": "By submitting this form, you acknowledge our",
  "privacy.consentLinkLabel": "Privacy Policy",
  "privacy.consentSuffix":
    ". Your data will be used to handle the request you made.",
  "schedule.successTitle": "Request sent!",
  "schedule.successSubtitle":
    "Your request has been sent. Keep an eye on your email — we'll let you know as soon as the partner confirms the time.",
  "schedule.successNotice":
    "Your proposal's conditions are guaranteed until {{date}}.",
  "schedule.fullAddressNotice": "Full address sent upon confirmation.",
  "schedule.postStep0Label": "Request sent",
  "schedule.postStep0Time": "Done",
  "schedule.toBeConfirmed": "to be confirmed",
  "schedule.postStep1Label": "Visit the office",
  "schedule.postStep2Label": "Final proposal",
  "schedule.postStep3Label": "Sign and move in",
  "schedule.postStep1Time": "From 48h",
  "schedule.postStep2Time": "48h after the visit",
  "schedule.postStep3Time": "Up to 15 days",
  "schedule.confirmationOverline": "Next steps",
  "schedule.confirmationTitle": "From interest to move-in, *in just days*.",
  "schedule.confirmationSubtitle":
    "Follow what happens next until you move into the new office.",
  "schedule.recapOverline": "Woba Network",
  "schedule.recapHeading": "You hire an office.\nYou get an entire country.",
  "schedule.recapSubheading":
    "5% of your contract value becomes a bonus in credits, so your team isn't tied to a single address. They can use meeting rooms, workstations and event spaces in any city in Brazil.",
  "schedule.recapPricePre": "And the price?",
  "schedule.recapPriceBold": "Always the same as negotiating directly",
  "schedule.recapPricePost": "— at no extra cost.",
  "schedule.recapStat1Value": "+3,000",
  "schedule.recapStat1Label": "flexible offices",
  "schedule.recapStat2Value": "+4,200",
  "schedule.recapStat2Label": "meeting rooms",
  "schedule.recapStat3Value": "+1,000",
  "schedule.recapStat3Label": "event spaces",
  "schedule.recapStat4Value": "+400",
  "schedule.recapStat4Label": "cities in LatAm",
  "schedule.recapType1Title": "Workstations",
  "schedule.recapType1DescPre": "1 day pass at any of the",
  "schedule.recapType1DescCount": "+23,000",
  "schedule.recapType1DescPost": "workstations",
  "schedule.recapType2Title": "Meeting Rooms",
  "schedule.recapType2DescPre": "1 hour at any of the",
  "schedule.recapType2DescCount": "+4,200",
  "schedule.recapType2DescPost": "rooms",
  "schedule.recapType3Title": "Event Spaces",
  "schedule.recapType3DescPre": "1 day pass at any of the",
  "schedule.recapType3DescCount": "+1,000",
  "schedule.recapType3DescPost": "spaces",
  "schedule.recapType4Title": "Private Offices",
  "schedule.recapType4DescPre": "1 month at any of the",
  "schedule.recapType4DescCount": "+5,200",
  "schedule.recapType4DescPost": "offices",
  "schedule.recapCredits": "credits",
  "schedule.recapCreditsPerPerson": "credits/person",
  "schedule.recapCreditsPerPosition": "credits/position",

  // ── Spaces (additional) ──
  "spaces.option": "Private Office {{number}}",
  "spaces.ofTotal": "of {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurants",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Pharmacies",
  "poi.transport": "Transport",
  "poi.hotel": "Hotels",
  "poi.parking": "Parking",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "this space",

  // ── CreditInfoDrawer ──
  "creditDrawer.viewDetails": "View credit details",
  "creditDrawer.title": "Credits included in your contract",
  "creditDrawer.subtitle":
    "5% of your contract value becomes a bonus in credits.",
  "creditDrawer.creditsLabel": "Credits/month included*",
  "creditDrawer.creditsValue": "+{{credits}} credits",
  "creditDrawer.creditsFootnote":
    "* Credits aren't cumulative: anything unused in the month doesn't carry over to the next one.",
  "creditDrawer.howItWorksBody":
    "Your team isn't tied to this address: with the included credits, they can use meeting rooms, workstations and event spaces in any city in Brazil — without paying anything extra for it.",
  "creditDrawer.formatMeetingRoomTitle": "Meeting rooms",
  "creditDrawer.formatMeetingRoomBody":
    "{{credits}} credits are worth {{hours}}h of meeting room time for up to 4 people — this changes with room size.",
  "creditDrawer.formatCoworkingTitle": "Coworking (shared workstations)",
  "creditDrawer.formatCoworkingBody":
    "{{credits}} credits are worth {{credits}} days of access to shared spaces.",
  "creditDrawer.formatPrivateOfficeTitle": "Private offices by the day",
  "creditDrawer.formatPrivateOfficeBody":
    "Use credits to set up temporary spaces for projects or teams.",
  "creditDrawer.formatEventSpaceTitle": "Event and training spaces",
  "creditDrawer.formatEventSpaceBody":
    "Book larger rooms and auditoriums using multiple credits.",
  "creditDrawer.quadroHoursLabel": "per month",
  "creditDrawer.quadroDaysLabel": "days/month",
  "creditDrawer.onRequest": "On request",
  "creditDrawer.practiceTitle": "In practice",
  "creditDrawer.practiceDayToDayTitle": "How this applies to your day-to-day",
  "creditDrawer.practiceBullet1": "Your office stops being a single address",
  "creditDrawer.practiceBullet2":
    "You pick the right format for each moment, no new contract needed",
  "creditDrawer.practiceBullet3":
    "The credit is already included — no extra cost on your invoice",
  "creditDrawer.practiceCta":
    "Instead of contracting more space, you use credits when you need them.",

  // ── RoomPriceCard (price card in the room details drawer, desktop) ──
  "priceCard.monthlyLabel": "Monthly office fee",
  "priceCard.monthlyLabelWithScope": "Monthly fee - {{scope}}",
  "priceCard.perPositionValue": "{{price}} / position",

  // ── CreditInfoDrawer, "extra credits" variant (ENT, PED-12653) ──
  "extraCreditDrawer.viewDetails": "View extra credit details",
  "extraCreditDrawer.title": "Extra credits in your contract",
  "extraCreditDrawer.subtitle":
    "An optional credit package, on top of the included ones, to use across the Woba network.",
  "extraCreditDrawer.creditsLabel": "Extra credits/month*",
  "extraCreditDrawer.monthlyValueLabel": "Package value/month",
  "extraCreditDrawer.howItWorksBody":
    "On top of the included credits, this package gives your team more credits per month for meeting rooms, workstations and event spaces in any city in Brazil — and it is part of your monthly total.",
  "extraCreditDrawer.practiceBullet3":
    "The package is optional and joins your monthly total — no separate contract",

  // ── PriceGuaranteeModal ──
  "priceGuarantee.title": "Best price guarantee",
  "priceGuarantee.subtext": "Same price as the partner, up to 15% off",
  "priceGuarantee.bodyPre":
    "We negotiate directly with the partner to get you the best price on your proposal. We get you ",
  "priceGuarantee.bodyBold": "up to 15% off",
  "priceGuarantee.bodyPost": " the office's value.",
  "priceCard.creditsLabel": "Credits included",
  "priceCard.freeBadge": "free",
  "priceCard.creditsSubtext":
    "Equivalent to {{credits}} office day passes/month",
  "priceCard.flexCreditsLabel": "Extra credits (optional)",
  "priceCard.setupFeeLabel": "Setup Fee",
  "priceCard.setupFeeCaption": "Paid once",
  "priceCard.licenseFeeLabel": "License Fee",
  "priceCard.licenseFeeSemiannualCaption": "Paid semiannually",
  "priceCard.licenseFeeAnnualCaption": "Paid annually",
  "priceCard.helpLink": "Still have questions? Talk to us",
  "priceCard.scheduleVisit": "Request a visit",
  "priceCard.makeProposal": "Make a proposal",

  // ── ProposalMakingStep ("Make a proposal" flow) ──
  "proposalMaking.hub.overline": "Make your proposal",
  "proposalMaking.hub.headline": "You *decide* how to move forward.",
  "proposalMaking.hub.subtitle":
    "Review the pricing and room summary before choosing the next steps for {{companyName}}.",
  "proposalMaking.hub.subtitleNoCompany":
    "Review the pricing and room summary before choosing the next steps.",
  "proposalMaking.subStepDefine": "Proposal terms",
  "proposalMaking.subStepAdjust": "Price and space adjustments",
  "proposalMaking.subStepReview": "Identification",
  "proposalMaking.subStepAriaLabel": "Proposal steps",
  "proposalMaking.choose.pickerLabel": "Choose an option",
  "proposalMaking.choose.acceptLabel": "Accept conditions",
  "proposalMaking.choose.acceptDescription":
    "Accepting the current conditions speeds up the review and puts your proposal ahead of ones still being negotiated.",
  "proposalMaking.choose.negotiateLabel":
    "Negotiate value or request an adjustment",
  "proposalMaking.choose.negotiateDescription":
    "Propose a different value or request adjustments to the space — our team reviews and replies within 2 business days.",
  "proposalMaking.negotiate.overline": "Negotiate value",
  "proposalMaking.negotiate.headline": "Propose the *right* value for you.",
  "proposalMaking.negotiate.subtitle":
    "Adjust the proposed monthly value and tell our team why it works for you — the closer to the current value, the higher the chance of a quick acceptance from the partner.",
  "proposalMaking.negotiate.subtitleNoPrice":
    "Tell our team what you'd like to propose and why it works for you — they'll review it with the partner and get back to you.",
  "proposalMaking.negotiate.sectionTitle": "Price adjustment",
  "proposalMaking.negotiate.fieldLabel": "Your proposal",
  "proposalMaking.negotiate.fieldPlaceholder": "0",
  "proposalMaking.negotiate.goodProposalTitle": "Great proposal!",
  "proposalMaking.negotiate.goodProposalDescription":
    "A value at or above the current one has a high chance of being accepted.",
  "proposalMaking.negotiate.moderateDiscountTitle": "Good chance of acceptance",
  "proposalMaking.negotiate.moderateDiscountDescription":
    "Discounts of up to 15% have a good chance of being approved by the partner.",
  "proposalMaking.negotiate.highDiscountTitle": "High discount",
  "proposalMaking.negotiate.highDiscountDescription":
    "Discounts above 15% are harder to approve — the response may take longer.",
  "proposalMaking.negotiate.discountBadge": "-{{percent}}%",
  "proposalMaking.negotiate.increaseBadge": "+{{percent}}%",
  "proposalMaking.negotiate.reasonLabel":
    "Tell us the reason for your proposal",
  "proposalMaking.negotiate.reasonPlaceholder":
    "Tell us the reason for the value adjustment...",
  "proposalMaking.negotiate.flexCreditsLabel": "Extra credits per month",
  "proposalMaking.negotiate.flexCreditsUnit": "credits",
  "proposalMaking.negotiate.flexCreditsHelper":
    "Comes to {{value}}/month. The negotiation above applies to the monthly rent only.",
  "proposalMaking.adjustmentsChoice.pickerLabel": "Space setup adjustment",
  "proposalMaking.adjustmentsChoice.overline": "Room adjustments",
  "proposalMaking.adjustmentsChoice.headline": "Need any *adjustments*?",
  "proposalMaking.adjustmentsChoice.subtitle":
    "You can ask to remove, install, repair or replace an item.",
  "proposalMaking.adjustmentsChoice.noneLabel": "No adjustments",
  "proposalMaking.adjustmentsChoice.noneBadge": "Higher chance of approval",
  "proposalMaking.adjustmentsChoice.someLabel": "With adjustments",
  "proposalMaking.adjustmentsChoice.someBadge":
    "Subject to the space's evaluation",
  "proposalMaking.adjustmentType.overline": "Adjustment type",
  "proposalMaking.adjustmentType.headline": "What type of *adjustment*?",
  "proposalMaking.adjustmentType.subtitle":
    "You can add one adjustment at a time.",
  "proposalMaking.adjustmentType.layout": "Layout",
  "proposalMaking.adjustmentType.layoutDescription":
    "Remove or install an item, or change/replace something in the space.",
  "proposalMaking.adjustmentType.maintenance": "Maintenance or repair",
  "proposalMaking.adjustmentType.maintenanceDescription":
    "Fix or perform maintenance on something that already exists.",
  "proposalMaking.adjustmentType.positions": "Positions",
  "proposalMaking.adjustmentType.positionsDescription":
    "Increase or decrease the office's number of positions.",
  "proposalMaking.adjustmentDescription.overline": "Adjustment details",
  "proposalMaking.adjustmentDescription.headline": "Describe what you *need*.",
  "proposalMaking.adjustmentDescription.subtitle":
    "The more detail, the faster we can review your request.",
  "proposalMaking.adjustmentDescription.placeholder":
    "Describe what you need...",
  "proposalMaking.adjustmentDescription.continueLabel": "Review",
  "proposalMaking.review.overline": "Almost there",
  "proposalMaking.review.headline": "Review your *proposal*.",
  "proposalMaking.review.subtitle":
    "Confirm the pricing and adjustments before sending it to our team.",
  "proposalMaking.review.bannerAccepted": "This is a great proposal!",
  "proposalMaking.review.bannerNegotiating":
    "Your proposal will be sent for our partner's review.",
  "proposalMaking.review.adjustmentsTitle": "Requested adjustments",
  "proposalMaking.review.addAdjustment": "Add adjustment",
  "proposalMaking.review.removeAdjustment": "Remove",
  "proposalMaking.review.identificationTitle": "Your details",
  "proposalMaking.review.occupancyDateLabel": "Expected occupancy date",
  "proposalMaking.review.contractTermLabel": "Contract term",
  "proposalMaking.review.contractTermOption.12": "12 months",
  "proposalMaking.review.contractTermOption.24": "24 months",
  "proposalMaking.review.contractTermOption.36": "36 months",
  "proposalMaking.review.contractTermOption.48": "48 months",
  "proposalMaking.review.contractTermOption.other": "Other",
  "proposalMaking.review.contractTermCustomPlaceholder": "How many months?",
  "proposalMaking.review.submitLabel": "Confirm and send",
  "proposalMaking.sent.overline": "Next steps",
  "proposalMaking.sent.headline": "Proposal sent!",
  "proposalMaking.sent.subtitle": "Your proposal was sent to the Woba team.",
  "proposalMaking.sent.notice": "This proposal is valid until {{date}}.",
  "proposalMaking.sent.roomStatusLabel": "Proposal sent",
  "proposalMaking.sent.postStep0Label": "Proposal sent",
  "proposalMaking.sent.postStep0Time": "Done",
  "proposalMaking.sent.postStep1Label": "Under review",
  "proposalMaking.sent.postStep1Time": "Response within 2 business days",
  "proposalMaking.sent.postStep2Label": "Approval",
  "proposalMaking.sent.postStep2Time": "After partner's response",
  "proposalMaking.sent.postStep3Label": "Sign and move in",
  "proposalMaking.sent.postStep3Time": "Up to 15 days",

  // ── "Custom Office" card (PED-13464) + briefing flow ──
  "customOffice.card.title": "Prefer an office tailor-made for your company?",
  "customOffice.card.subtitle":
    "We can go beyond these options. If you want something more exclusive, we set up a tailor-made office, without you having to deal with brokers, construction and suppliers.",
  "customOffice.card.bullet1": "We find the ideal site",
  "customOffice.card.bullet2": "100% custom design",
  "customOffice.card.bullet3": "Full operations included",
  "customOffice.card.bullet4": "All Woba benefits",
  "customOffice.card.cta": "Build my office",
  // Qualifies before the click (the team's worry: a client coming in
  // expecting one more ready office): exclusive project, higher investment.
  "customOffice.card.footnote":
    "Exclusive project · Investment above a ready office",
  // Briefing flow — the 5 questions from the "Briefing rápido · Camada
  // 1" reference, rewritten for a self-serve form inside the proposal: no
  // internal jargon, no abbreviations, every field explains itself.
  "customOffice.hub.overline": "Custom office · 3 steps",
  "customOffice.hub.headline": "Let's design your team's *place*",
  "customOffice.hub.subtitle":
    "Answer five quick questions about what your team needs and leave your contact details. It takes about 10 minutes and is enough for Woba to start looking for the ideal space for you.",
  "customOffice.hub.noticeTitle": "Before you start",
  "customOffice.hub.noticeBody":
    "Tailor-made is an exclusive project: we find the property, design the office for your team and run its operations. Because of that, the investment is usually above a ready office, and moving in takes longer, since there may be a build-out. If a ready office works for you, it remains the fastest and most economical path.",
  "customOffice.hub.previewTitle": "What you'll answer",
  "customOffice.hub.footnote":
    "After you send it, our team gets in touch to work out the details.",
  "customOffice.stepper.ariaLabel": "Form steps",
  // 3 stages: the space (questions 01 and 02), team and priorities (03 to
  // 05) and "Your details" on its own — each question is still its own card.
  "customOffice.stepper.place": "The space",
  "customOffice.stepper.routine": "Team and priorities",
  "customOffice.stepper.contact": "Your details",
  "customOffice.stage.overline": "Step {{number}} of {{total}}",
  "customOffice.stage.place.title": "What does the ideal office look like?",
  "customOffice.stage.place.subtitle":
    "Where it is, for how many people and what kind of property you're after.",
  "customOffice.stage.routine.title": "How will your team use the space?",
  "customOffice.stage.routine.subtitle":
    "The day to day, what can't be missing and references that help us get it right.",
  "customOffice.stage.contact.title": "Your details",
  "customOffice.stage.contact.subtitle":
    "Who we should get back to with the custom proposal.",
  "customOffice.q1.title": "Where, and for how many?",
  "customOffice.q1.subtitle":
    "Where the office should be, for how many people, and by when.",
  "customOffice.q1.regionLabel": "City and desired region / neighborhood",
  "customOffice.q1.regionPlaceholder":
    "E.g.: São Paulo, Pinheiros or Faria Lima",
  "customOffice.q1.peopleLabel": "Number of people",
  "customOffice.q1.peoplePlaceholder": "E.g.: 20",
  "customOffice.q1.sizeLabel": "Approximate size (sqm)",
  "customOffice.q1.sizePlaceholder": "If you know. E.g.: 200",
  "customOffice.q1.deadlineLabel": "Move-in deadline",
  "customOffice.q1.deadlinePlaceholder": "E.g.: within 60 days, by March",
  "customOffice.requiredHint": "Fill in the required fields to continue",
  "customOffice.q2.title": "What is the ideal place like?",
  "customOffice.q2.subtitle":
    "The building's standard, the type of property, and whether you need parking.",
  "customOffice.q2.standardLabel": "Desired standard",
  "customOffice.q2.standardOption.premium": "High-end (AAA)",
  "customOffice.q2.standardOption.intermediate": "Mid-range",
  "customOffice.q2.standardOption.simple": "Simpler",
  "customOffice.q2.buildingTypeLabel": "Type of property",
  "customOffice.q2.buildingTypeOption.corporateSlab":
    "Floor in a corporate building",
  "customOffice.q2.buildingTypeOption.streetBuilding":
    "House or street-front building",
  "customOffice.q2.buildingTypeOption.either": "Either",
  "customOffice.q2.parkingLabel": "Parking",
  "customOffice.q2.parkingPlaceholder": "How many spots do you need? E.g.: 5",
  "customOffice.q2.readinessLabel":
    "Would you rather a ready-to-use office, or are you open to construction?",
  "customOffice.q2.readinessOption.readyToUse": "Ready to use",
  "customOffice.q2.readinessOption.openToCustomBuild": "Open to a custom build",
  "customOffice.q3.title": "What is the team's day to day like?",
  "customOffice.q3.subtitle": "How many meeting rooms the team needs.",
  "customOffice.q3.meetingSmallLabel": "Small (up to 4 people)",
  "customOffice.q3.meetingSmallPlaceholder": "How many?",
  "customOffice.q3.meetingMediumLabel": "Medium (6 to 8 people)",
  "customOffice.q3.meetingMediumPlaceholder": "How many?",
  "customOffice.q3.meetingLargeLabel": "Large (10 or more)",
  "customOffice.q3.meetingLargePlaceholder": "How many?",
  "customOffice.q3.meetingRoomsLabel": "Meeting rooms",
  "customOffice.q4.title": "What are the priorities?",
  "customOffice.q4.subtitle":
    "The basic amenities, what absolutely can't be missing and what would be nice to have.",
  // Amenities moved here from question 03 (it's a priority
  // pick, not routine). The must-have examples must be things the chips
  // don't cover, or the client just repeats the list.
  "customOffice.q4.amenitiesLabel": "Mandatory basic amenities",
  "customOffice.q4.amenitiesOption.kitchen": "Kitchen or cafeteria",
  "customOffice.q4.amenitiesOption.buildingReception": "Building reception",
  "customOffice.q4.amenitiesOption.publicTransport": "Public transport nearby",
  "customOffice.q4.amenitiesOption.restaurants": "Restaurants nearby",
  "customOffice.q4.nonNegotiableLabel": "What else can't be missing",
  "customOffice.q4.nonNegotiableHelp":
    "List one item per line, starting with the most important. Examples: pet-friendly, environmental certification of the building, loading and unloading area, secure room, 24-hour reception.",
  "customOffice.q4.priority1Label": "1st",
  "customOffice.q4.priority2Label": "2nd",
  "customOffice.q4.priority3Label": "3rd",
  "customOffice.q4.niceToHaveLabel": "Nice to have",
  "customOffice.q4.niceToHaveHelp":
    "Optional. Things that would make a difference but don't decide the choice.",
  "customOffice.q4.niceToHavePlaceholder":
    "E.g.: bike racks, gym in the building, rooftop terrace",
  "customOffice.q5.title": "Have you seen a property you liked?",
  "customOffice.q5.subtitle":
    "Examples of what you liked or ruled out help us get it right faster.",
  "customOffice.q5.likedLabel":
    "Any office or building you've seen and liked? Tell us what tipped it: address, building, layout, price.",
  "customOffice.q5.likedPlaceholder":
    "E.g.: building X on Faria Lima, for the location and the layout",
  "customOffice.q5.discardedLabel": "Any you ruled out? Why?",
  "customOffice.q5.discardedPlaceholder":
    "E.g.: building Y, because it had no parking",
  // Last stage: the same 4 identification fields as the proposal ("Your
  // details" on Review), reusing the ContactForm and its labels.
  "customOffice.contact.hint": "Fill in your details to send",
  "customOffice.submitLabel": "Send answers",
  "customOffice.sent.overline": "Next steps",
  // Same final screen as the visit/proposal flows: what comes next, the
  // summary card of what was asked, and the network value recap below.
  "customOffice.sent.summaryLabel": "Answers sent",
  "customOffice.sent.postStep0Label": "Answers sent",
  "customOffice.sent.postStep0Time": "Done",
  "customOffice.sent.postStep1Label": "Property search",
  "customOffice.sent.postStep1Time": "Within 7 days",
  "customOffice.sent.postStep2Label": "Custom proposal",
  "customOffice.sent.postStep2Time": "After the search",
  "customOffice.sent.postStep3Label": "Sign and move in",
  "customOffice.sent.postStep3Time": "Timeline to be agreed",
  "customOffice.summary.likedLabel": "Liked",
  "customOffice.summary.discardedLabel": "Ruled out",
  "customOffice.sent.headline": "Answers sent!",
  "customOffice.sent.subtitle":
    "Our team will use your answers to put together a custom proposal. You'll receive it within 7 days.",

  // ── Rating (recommendation feedback) ──
  "rating.prompt": "Did we get it right?",
  "rating.drawerPrompt": "Did we get this office right?",
  "rating.thumbUp": "I liked this office",
  "rating.thumbDown": "I didn't like this office",
  "rating.modalTitlePositive": "Great! We got the recommendation right",
  "rating.modalTitleNegative": "We didn't quite get the recommendation right",
  "rating.modalSupportPositive":
    "Your feedback helps us understand what worked for you.",
  "rating.modalSupportNegative":
    "We have more options for you. Your feedback helps us bring the most accurate ones.",
  "rating.questionNegative": "What didn't fit?",
  "rating.questionPositive": "What did you like the most?",
  "rating.subReasonsLabel": "What's missing?",
  "rating.commentLabel": "Want to tell us more? (optional)",
  "rating.commentPlaceholder": "Write here...",
  "rating.submit": "Send feedback",
  "rating.confirmationTitle": "Got it!",
  "rating.confirmationBody":
    "We'll use your feedback to improve the next suggestions.",
  "rating.close": "Close",
  "rating.reason.photos": "The photos don't help me understand the office",
  "rating.reason.capacity": "It doesn't fit the number of people I need",
  "rating.reason.location": "The area doesn't work for me",
  "rating.reason.price": "Not worth the asking price",
  "rating.reason.layoutCondition":
    "The layout or condition of the office didn't appeal to me",
  "rating.reason.missingRequirements": "It's missing something I need",
  "rating.reason.capacityFit": "Fits my team well",
  "rating.reason.locationGreat": "The area is great for me",
  "rating.reason.visualAppeal": "I liked the look of the office",
  "rating.reason.amenitiesComplete": "It has everything I need",
  "rating.reason.priceValue": "Good value for money",
  "rating.subReason.amenities": "Amenities",
  "rating.subReason.equipment": "Equipment",
  "rating.subReason.accessibility": "Accessibility",
  "rating.subReason.other": "Other features",
};
